// src/contexts/onboarding-data-context.tsx
import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
  useEffect,
} from "react";
import { OnboardingStorageData } from "@/types/onboarding";
import {
  fetchAllOnboardingData,
  DEV_FALLBACK_WEB_APP_URL,
} from "@/lib/onboarding-data-utils";
import { AccountContext } from "./account-context";
import { db } from "@/services/db";
import { Account } from "@/types/metrics";

type LoadingStatus = "idle" | "loading" | "polling" | "error" | "success";

interface OnboardingDataContextType {
  data: OnboardingStorageData | null;
  loadingStatus: LoadingStatus;
  error: string | null;
  fetchOnboardingData: (urlOverride?: string) => Promise<void>;
  pollForDataReady: () => Promise<boolean>;
  hasDataBeenFetched: boolean;
  clearOnboardingData: () => void;
}

// Exported for dev tools that need to check if context exists without throwing
export const OnboardingDataContext = createContext<
  OnboardingDataContextType | undefined
>(undefined);

export const OnboardingDataProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [data, setData] = useState<OnboardingStorageData | null>(null);
  const [loadingStatus, setLoadingStatus] = useState<LoadingStatus>("idle");
  const [error, setError] = useState<string | null>(null);
  const accountContext = useContext(AccountContext);
  const [activeAccount, setActiveAccount] = useState<Account | null>(null);
  const [hasDataBeenFetched, setHasDataBeenFetched] = useState(false);

  useEffect(() => {
    if (accountContext && accountContext.activeDexieId) {
      db.getAccount(accountContext.activeDexieId).then((account) => {
        if (account) {
          setActiveAccount(account);
        }
      });
    }
  }, [accountContext]);

  const fetchOnboardingData = useCallback(
    async (urlOverride?: string) => {
      console.log("[ONBOARDING] fetchOnboardingData called", {
        urlOverride,
        hasActiveAccount: !!activeAccount,
        timestamp: new Date().toISOString(),
      });

      // In development mode, always use the fallback URL unless explicitly overridden
      let urlToUse = urlOverride;
      if (!urlToUse) {
        if (process.env.NODE_ENV === "development") {
          urlToUse = DEV_FALLBACK_WEB_APP_URL;
          console.log(
            "[ONBOARDING] Development mode: using fallback URL",
            urlToUse,
          );
        } else {
          urlToUse = activeAccount?.webAppUrl;
        }
      }

      // We can proceed as long as we have *some* URL – the account is optional in dev / debug flows.
      if (!urlToUse) {
        setError("Web App URL is not available.");
        setLoadingStatus("error");
        return;
      }

      const effectiveDexieId = activeAccount?.dexieId ?? "DEV_ONBOARDING";

      if (process.env.NODE_ENV === "development") {
        // eslint-disable-next-line no-console
        console.log("[ONBOARDING] Triggering data fetch", {
          effectiveDexieId,
          urlToUse,
          hasActiveAccount: !!activeAccount,
          usingFallback: urlToUse === DEV_FALLBACK_WEB_APP_URL,
        });
      }

      setLoadingStatus("loading");
      setError(null);

      try {
        const onboardingData = await fetchAllOnboardingData(
          effectiveDexieId,
          urlToUse,
        );
        setData(onboardingData);
        setLoadingStatus("success");
        setHasDataBeenFetched(true);
        console.log("[ONBOARDING] Fetch successful");

        // Store account info for potential transfer when user signs up
        if (onboardingData?.account?.[0]) {
          const accountInfo = onboardingData.account[0];
          const tempAccountData = {
            accountCID: accountInfo.accountCID,
            accountName: accountInfo.accountName,
            webAppUrl: urlToUse,
            businessMode: "ecomm", // Default, user can change later
            timestamp: new Date().toISOString(),
          };
          localStorage.setItem(
            "temp_account_data",
            JSON.stringify(tempAccountData),
          );
          console.log(
            "[ONBOARDING] Stored account info for potential transfer:",
            accountInfo.accountCID,
          );
        }

        // Store full onboarding data in IndexedDB for persistence during navigation
        try {
          await db.onboardingData.put({
            ...onboardingData,
            dexieId: effectiveDexieId,
            timestamp: new Date().toISOString(),
          });
          console.log("[ONBOARDING] Stored onboarding data in IndexedDB");
        } catch (dbError) {
          console.warn("[ONBOARDING] Failed to store in IndexedDB:", dbError);
        }
      } catch (e) {
        console.error("[Onboarding] Failed to fetch onboarding data:", e);
        setError(e instanceof Error ? e.message : "An unknown error occurred.");
        setLoadingStatus("error");
      }
    },
    [activeAccount],
  );

  const pollForDataReady = useCallback(async (): Promise<boolean> => {
    // In development mode, use fallback URL for polling
    let urlToUse: string;
    if (process.env.NODE_ENV === "development") {
      urlToUse = DEV_FALLBACK_WEB_APP_URL;
    } else {
      if (!activeAccount || !activeAccount.webAppUrl) {
        setError("Active account or Web App URL is not available for polling.");
        setLoadingStatus("error");
        return false;
      }
      urlToUse = activeAccount.webAppUrl;
    }

    setLoadingStatus("polling");
    setError(null);

    try {
      // We just need to check if one of the tabs is available.
      const response = await fetch(`${urlToUse}?tab=on_performance`);
      if (response.ok) {
        const testData = await response.json();
        // A valid response should be an array. An error from the script is often an object.
        if (Array.isArray(testData)) {
          return true;
        }
      }
      return false;
    } catch (e) {
      // This is expected if the sheet isn't ready yet.
      return false;
    }
  }, [activeAccount]);

  const clearOnboardingData = useCallback(() => {
    setData(null);
    setLoadingStatus("idle");
    setError(null);
    setHasDataBeenFetched(false);
    // Clear from IndexedDB
    db.onboardingData.clear().catch(console.warn);
    console.log("[ONBOARDING] Cleared onboarding data");
  }, []);

  // Load cached data from IndexedDB on mount
  useEffect(() => {
    const loadCachedData = async () => {
      try {
        const cachedData = await db.onboardingData.orderBy("timestamp").last();
        if (cachedData) {
          setData(cachedData);
          setLoadingStatus("success");
          setHasDataBeenFetched(true);
          console.log("[ONBOARDING] Loaded cached data from IndexedDB");
        }
      } catch (error) {
        console.warn("[ONBOARDING] Failed to load cached data:", error);
      }
    };

    if (!hasDataBeenFetched && loadingStatus === "idle") {
      loadCachedData();
    }
  }, [hasDataBeenFetched, loadingStatus]);

  const value = {
    data,
    loadingStatus,
    error,
    fetchOnboardingData,
    pollForDataReady,
    hasDataBeenFetched,
    clearOnboardingData,
  };

  return (
    <OnboardingDataContext.Provider value={value}>
      {children}
    </OnboardingDataContext.Provider>
  );
};

export const useOnboardingData = () => {
  const context = useContext(OnboardingDataContext);
  if (context === undefined) {
    throw new Error(
      "useOnboardingData must be used within an OnboardingDataProvider",
    );
  }
  return context;
};
